import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Instagram, Facebook, Twitter } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useBranding } from "../../contexts/BrandingContext";

// Always use same-origin API calls so cookies stay first-party across
// preview / emergent.host / custom domains (like tablecy.com).
const API = "/api";

export default function Footer() {
  const [links, setLinks] = useState(null);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { branding } = useBranding();
  const brandName = (lang === "el" && branding?.brand_name_el) || branding?.brand_name || "TableCy";

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const res = await axios.get(`${API}/footer-links`);
        setLinks(res.data);
      } catch (error) {
        // Fallback to static links
        setLinks(null);
      }
    };
    fetchLinks();
  }, []);

  const groupedLinks = links
    ? {
        explore: links.filter((l) => l.section === "explore"),
        for_venues: links.filter((l) => l.section === "for_venues"),
        company: links.filter((l) => l.section === "company"),
      }
    : null;

  const localizedTitle = (link) => (lang === "el" && link.title_el) || link.title;

  const renderLink = (link) => {
    const isExternal = link.url.startsWith("http");
    const label = localizedTitle(link);
    if (isExternal) {
      return (
        <li key={link.link_id || link.url}>
          <a href={link.url} className="text-background/70 hover:text-background transition-colors" target="_blank" rel="noreferrer">
            {label}
          </a>
        </li>
      );
    }
    return (
      <li key={link.link_id || link.url}>
        <Link to={link.url} className="text-background/70 hover:text-background transition-colors">
          {label}
        </Link>
      </li>
    );
  };

  // Fallback static links (with bilingual fallback titles)
  const staticLinks = {
    explore: [
      { url: "/venues", title: "All Venues", title_el: "Όλοι οι Χώροι" },
      { url: "/events", title: "Events", title_el: "Εκδηλώσεις" },
      { url: "/collections", title: "Collections", title_el: "Συλλογές" },
      { url: "/map", title: "Map", title_el: "Χάρτης" },
    ],
    for_venues: [
      { url: "/page/for-venues", title: "List Your Venue", title_el: "Καταχώρηση Χώρου" },
      { url: "/login", title: "Venue Login", title_el: "Σύνδεση Χώρου" },
      { url: "/pricing", title: "Pricing", title_el: "Τιμολόγηση" },
    ],
    company: [
      { url: "/page/about", title: "About Us", title_el: "Σχετικά με εμάς" },
      { url: "/page/contact", title: "Contact", title_el: "Επικοινωνία" },
      { url: "/page/privacy", title: "Privacy Policy", title_el: "Πολιτική Απορρήτου" },
      { url: "/page/terms", title: "Terms of Service", title_el: "Όροι Χρήσης" },
    ],
  };

  const exploreLinks = groupedLinks?.explore?.length ? groupedLinks.explore : staticLinks.explore;
  const venueLinks = groupedLinks?.for_venues?.length ? groupedLinks.for_venues : staticLinks.for_venues;
  const companyLinks = groupedLinks?.company?.length ? groupedLinks.company : staticLinks.company;

  return (
    <footer className="bg-foreground text-background py-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2" data-testid="footer-brand-logo">
              {branding?.use_image && branding?.logo_url ? (
                <img src={branding.logo_url} alt={brandName} className="h-7 w-7 object-contain" />
              ) : branding?.logo_svg ? (
                <span className="h-7 w-7 inline-flex items-center justify-center" dangerouslySetInnerHTML={{ __html: branding.logo_svg }} />
              ) : (
                <MapPin className="h-7 w-7" />
              )}
              <span className="text-xl font-semibold">{brandName}</span>
            </Link>
            <p className="text-sm text-background/70">
              {t("footer:tagline", { defaultValue: "Discover and book the best restaurants, bars, and tavernas in Cyprus." })}
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-background/70 hover:text-background transition-colors" data-testid="footer-instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className="text-background/70 hover:text-background transition-colors" data-testid="footer-facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="#" className="text-background/70 hover:text-background transition-colors" data-testid="footer-twitter">
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="font-medium mb-4">{t("footer:section_explore", { defaultValue: "Explore" })}</h4>
            <ul className="space-y-3 text-sm">
              {exploreLinks.map(renderLink)}
            </ul>
          </div>

          {/* For Venues */}
          <div>
            <h4 className="font-medium mb-4">{t("footer:section_for_venues", { defaultValue: "For Venues" })}</h4>
            <ul className="space-y-3 text-sm">
              {venueLinks.map(renderLink)}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-medium mb-4">{t("footer:section_company", { defaultValue: "Company" })}</h4>
            <ul className="space-y-3 text-sm">
              {companyLinks.map(renderLink)}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background/20 text-center text-sm text-background/50">
          <p>&copy; {new Date().getFullYear()} {brandName}. {t("footer:rights")}</p>
        </div>
      </div>
    </footer>
  );
}
